import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormItem, FormMessage } from '@readcloud/ui/form';
import { Input } from '@readcloud/ui/input';
import { Label } from '@readcloud/ui/label';
import { Separator } from '@readcloud/ui/separator';
import { Subtle, Text } from '@readcloud/ui/typography';
import { Button } from '@readcloud/ui/button';
import { contentTypeConfig } from './schemas/uploadWizardConfig';
import { mimeTypeConfigsByType } from './types/mimeTypes';
import { X } from 'lucide-react';
import { ContentFieldData } from './schemas/formFields';

// Use the type for form errors
const UploadContentForm: React.FunctionComponent = ({}) => {
  const { control, formState, setValue, watch } =
    useFormContext<ContentFieldData>();

  // Can watch this since type is part of content. These 2 steps should essentially be thought of as a single step
  const type = watch('content.type') as keyof typeof contentTypeConfig;

  const extensions = React.useMemo(
    () => mimeTypeConfigsByType[type]?.map((type) => type.extension).join(','),
    [type]
  );

  const { fileUpload, urlUpload, fileUploadLabel, urlUploadLabel, accept } =
    contentTypeConfig[type]?.uploadContent;

  return (
    <div className="rc-flex rc-flex-col">
      {fileUpload && (
        <>
          <Controller
            name="content.file"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="file">{fileUploadLabel || 'Upload File'}</Label>
                {field.value ? (
                  <div className="rc-flex rc-items-center rc-gap-2 w-full rc-justify-between">
                    <Text>{field.value.name}</Text>
                    <Button
                      variant="icon"
                      onClick={() => setValue('content.file', null)}
                    >
                      <X />
                    </Button>
                  </div>
                ) : (
                  <Input
                    type="file"
                    id="file"
                    accept={accept || extensions}
                    onChange={(e) => field.onChange(e.target.files?.[0])}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                )}
                {formState.errors.content?.file && (
                  <FormMessage>
                    {formState.errors.content?.file.message}
                  </FormMessage>
                )}
                {formState.errors.content && (
                  <FormMessage>{formState.errors.content.message}</FormMessage>
                )}
              </FormItem>
            )}
          />
        </>
      )}

      {fileUpload && urlUpload && (
        <div className="rc-flex rc-gap-2 rc-items-center rc-mt-6 rc-mb-4 ">
          <Separator className="rc-flex-1" />
          <Subtle className=" rc-text-center">OR</Subtle>
          <Separator className="rc-flex-1" />
        </div>
      )}

      {urlUpload && (
        <Controller
          control={control}
          name="content.url"
          render={({ field }) => (
            <FormItem>
              <Label htmlFor="url">{urlUploadLabel || 'Content URL'}</Label>
              <Input
                type="text"
                id="url"
                onChange={(e) => field.onChange(e.target.value || null)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                value={field.value || ''}
              />
              {formState.errors.content?.url && (
                <FormMessage>
                  {formState.errors.content?.url.message}
                </FormMessage>
              )}
            </FormItem>
          )}
        />
      )}
    </div>
  );
};

export default UploadContentForm;
